import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';

const CalendarView = () => {
  const { 
    currentUser,
    selectedCohortId,
    cohorts,
    getCohortById,
    getEventsForCohort,
    addMasterCalendarEvent,
    addCohortCustomEvent,
    deleteCalendarEvent,
    deleteMasterCalendarEvent,
    setSelectedCohortId,
    loadCohortItemOverrides
  } = useLMS();

  // Load cohort overrides when cohort changes
  useEffect(() => {
    if (selectedCohortId && loadCohortItemOverrides) {
      loadCohortItemOverrides(selectedCohortId);
    }
  }, [selectedCohortId, loadCohortItemOverrides]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [newEvent, setNewEvent] = useState({
    type: 'other',
    title: '',
    date: '',
    startTime: '',
    endTime: '',
    description: '',
    // Master template fields
    eventScope: 'cohort', // 'master' or 'cohort'
    weekNumber: 1,
    dayOfWeek: 'Monday',
    alsoAddToModule: true, // Default to true for convenience
    // Recurring fields (for cohort-specific events)
    isRecurring: false,
    recurringType: 'weekly',
    recurringDays: [],
    recurringEndDate: '',
    recurringCount: 4
  });

  const weekdays = [
    { key: 'Sunday', label: 'Sun' },
    { key: 'Monday', label: 'Mon' },
    { key: 'Tuesday', label: 'Tue' },
    { key: 'Wednesday', label: 'Wed' },
    { key: 'Thursday', label: 'Thu' },
    { key: 'Friday', label: 'Fri' },
    { key: 'Saturday', label: 'Sat' }
  ];

  const isAdmin = currentUser?.role === 'admin';
  const isStaff = isAdmin || currentUser?.role === 'professor';
  
  // For students, use their cohort. For admins, use selected or let them pick
  // Also check currentUser.cohort_id (snake_case from database)
  const studentCohortId = currentUser?.cohortId || currentUser?.cohort_id;
  const activeCohortId = currentUser?.role === 'student' 
    ? studentCohortId 
    : selectedCohortId;
  
  const cohort = activeCohortId ? getCohortById(activeCohortId) : null;
  const events = activeCohortId ? getEventsForCohort(activeCohortId) : [];

  // Calendar helpers
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  // Helper to format date as YYYY-MM-DD in local time
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getEventsForDate = (date) => {
    const dateStr = formatLocalDate(date);
    return events.filter(event => event.date === dateStr);
  };

  const getEventColor = (type) => {
    const colors = {
      lecture: '#4FC3F7',
      assignment: '#FF7043',
      quiz: '#7E57C2',
      exam: '#E53935',
      other: '#66BB6A'
    };
    return colors[type] || '#9E9E9E';
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleDateClick = (date) => {
    // Get events for this day
    const dateStr = formatLocalDate(date);
    const dayEvents = events.filter(e => e.date === dateStr);
    
    setSelectedDate(date);
    setSelectedDayEvents(dayEvents);
    
    // Show day modal with events (for everyone)
    if (dayEvents.length > 0 || !isStaff) {
      setShowDayModal(true);
    }
    
    // For admin with no events, go straight to add modal
    if (isStaff && activeCohortId && dayEvents.length === 0) {
      // Calculate week number relative to cohort start
      let weekNum = 1;
      let dayName = weekdays[date.getDay()].key;
      
      if (cohort) {
        const startDate = parseLocalDate(cohort.startDate);
        const diffDays = Math.floor((date - startDate) / (1000 * 60 * 60 * 24));
        weekNum = Math.min(6, Math.max(1, Math.floor(diffDays / 7) + 1));
      }
      
      setNewEvent(prev => ({
        ...prev,
        date: formatLocalDate(date),
        weekNumber: weekNum,
        dayOfWeek: dayName
      }));
      setShowAddModal(true);
    }
  };
  
  const handleAddEventFromDayModal = () => {
    if (!isStaff || !activeCohortId) return;
    
    // Calculate week number relative to cohort start
    let weekNum = 1;
    let dayName = weekdays[selectedDate.getDay()].key;
    
    if (cohort) {
      const startDate = parseLocalDate(cohort.startDate);
      const diffDays = Math.floor((selectedDate - startDate) / (1000 * 60 * 60 * 24));
      weekNum = Math.min(6, Math.max(1, Math.floor(diffDays / 7) + 1));
    }
    
    setNewEvent(prev => ({
      ...prev,
      date: formatLocalDate(selectedDate),
      weekNumber: weekNum,
      dayOfWeek: dayName
    }));
    setShowDayModal(false);
    setShowAddModal(true);
  };

  const handleAddEvent = (e) => {
    e.preventDefault();
    if (activeCohortId) {
      if (newEvent.eventScope === 'master') {
        // Add to master template - applies to ALL cohorts
        addMasterCalendarEvent({
          type: newEvent.type,
          title: newEvent.title,
          weekNumber: parseInt(newEvent.weekNumber),
          dayOfWeek: newEvent.dayOfWeek,
          startTime: newEvent.startTime,
          endTime: newEvent.endTime,
          description: newEvent.description,
          isFromModule: false,
          alsoAddToModule: newEvent.alsoAddToModule // Pass the checkbox value
        });
      } else if (newEvent.isRecurring) {
        // Generate multiple cohort-specific events for recurring
        const events = generateRecurringEvents(newEvent);
        events.forEach(event => {
          addCohortCustomEvent(activeCohortId, event);
        });
      } else {
        // Single cohort-specific event
        addCohortCustomEvent(activeCohortId, newEvent);
      }
      setShowAddModal(false);
      setNewEvent({
        type: 'other',
        title: '',
        date: '',
        startTime: '',
        endTime: '',
        description: '',
        eventScope: 'cohort',
        weekNumber: 1,
        dayOfWeek: 'Monday',
        alsoAddToModule: true,
        isRecurring: false,
        recurringType: 'weekly',
        recurringDays: [],
        recurringEndDate: '',
        recurringCount: 4
      });
    }
  };

  // Helper to parse date string as local time (avoids UTC timezone shift)
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date();
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Generate recurring events based on settings
  const generateRecurringEvents = (eventData) => {
    const events = [];
    const startDate = parseLocalDate(eventData.date);
    
    if (eventData.recurringType === 'daily') {
      // Daily recurrence
      const endDate = eventData.recurringEndDate 
        ? parseLocalDate(eventData.recurringEndDate) 
        : new Date(startDate.getTime() + (eventData.recurringCount - 1) * 24 * 60 * 60 * 1000);
      
      let current = new Date(startDate);
      while (current <= endDate) {
        events.push({
          ...eventData,
          date: formatLocalDate(current),
          isRecurring: true,
          recurringId: `recurring-${Date.now()}`
        });
        current.setDate(current.getDate() + 1);
      }
    } else if (eventData.recurringType === 'weekly') {
      // Weekly on specific days
      const selectedDays = eventData.recurringDays.length > 0 
        ? eventData.recurringDays 
        : [weekdays[startDate.getDay()].key];
      
      const dayMap = {
        'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
        'Thursday': 4, 'Friday': 5, 'Saturday': 6
      };
      
      const endDate = eventData.recurringEndDate 
        ? parseLocalDate(eventData.recurringEndDate) 
        : new Date(startDate.getTime() + (eventData.recurringCount * 7) * 24 * 60 * 60 * 1000);
      
      let current = new Date(startDate);
      const recurringId = `recurring-${Date.now()}`;
      
      while (current <= endDate) {
        if (selectedDays.includes(weekdays[current.getDay()].key)) {
          events.push({
            type: eventData.type,
            title: eventData.title,
            date: formatLocalDate(current),
            startTime: eventData.startTime,
            endTime: eventData.endTime,
            description: eventData.description,
            isRecurring: true,
            recurringId
          });
        }
        current.setDate(current.getDate() + 1);
      }
    } else if (eventData.recurringType === 'biweekly') {
      // Every two weeks
      const endDate = eventData.recurringEndDate 
        ? parseLocalDate(eventData.recurringEndDate) 
        : new Date(startDate.getTime() + (eventData.recurringCount * 14) * 24 * 60 * 60 * 1000);
      
      let current = new Date(startDate);
      const recurringId = `recurring-${Date.now()}`;
      
      while (current <= endDate) {
        events.push({
          type: eventData.type,
          title: eventData.title,
          date: formatLocalDate(current),
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          description: eventData.description,
          isRecurring: true,
          recurringId
        });
        current.setDate(current.getDate() + 14);
      }
    } else if (eventData.recurringType === 'monthly') {
      // Monthly on same day of month
      const endDate = eventData.recurringEndDate 
        ? parseLocalDate(eventData.recurringEndDate) 
        : new Date(startDate);
      
      if (!eventData.recurringEndDate) {
        endDate.setMonth(endDate.getMonth() + eventData.recurringCount);
      }
      
      let current = new Date(startDate);
      const dayOfMonth = startDate.getDate();
      const recurringId = `recurring-${Date.now()}`;
      
      while (current <= endDate) {
        events.push({
          type: eventData.type,
          title: eventData.title,
          date: formatLocalDate(current),
          startTime: eventData.startTime,
          endTime: eventData.endTime,
          description: eventData.description,
          isRecurring: true,
          recurringId
        });
        current.setMonth(current.getMonth() + 1);
        // Handle months with fewer days
        current.setDate(Math.min(dayOfMonth, new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate()));
      }
    }
    
    return events;
  };

  // Generate preview text for recurring events
  const getRecurringPreview = () => {
    if (!newEvent.isRecurring || !newEvent.date) return '';
    
    const startDate = parseLocalDate(newEvent.date);
    let preview = '';
    
    switch (newEvent.recurringType) {
      case 'daily':
        preview = `Every day starting ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        break;
      case 'weekly':
        const days = newEvent.recurringDays.length > 0 
          ? newEvent.recurringDays.join(', ') 
          : weekdays[startDate.getDay()].key;
        preview = `Every week on ${days}`;
        break;
      case 'biweekly':
        preview = `Every 2 weeks on ${weekdays[startDate.getDay()].key}`;
        break;
      case 'monthly':
        preview = `Monthly on day ${startDate.getDate()}`;
        break;
    }
    
    if (newEvent.recurringEndDate) {
      const endDate = parseLocalDate(newEvent.recurringEndDate);
      preview += ` until ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      preview += ` (${newEvent.recurringCount} occurrences)`;
    }
    
    return preview;
  };

  const toggleRecurringDay = (day) => {
    setNewEvent(prev => ({
      ...prev,
      recurringDays: prev.recurringDays.includes(day)
        ? prev.recurringDays.filter(d => d !== day)
        : [...prev.recurringDays, day]
    }));
  };

  const handleDeleteEvent = (eventId, isFromMasterTemplate) => {
    const message = isFromMasterTemplate 
      ? 'Delete this event from ALL cohorts? (Master template event)'
      : 'Delete this event from this cohort?';
    
    if (window.confirm(message)) {
      if (isFromMasterTemplate) {
        // Extract original master event ID and delete from master
        const originalId = eventId.replace(`-${activeCohortId}`, '');
        deleteMasterCalendarEvent(originalId);
      } else {
        deleteCalendarEvent(activeCohortId, eventId);
      }
    }
  };

  // Build calendar grid
  const calendarDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(new Date(year, month, day));
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Upcoming events list
  const upcomingEvents = events
    .filter(e => new Date(e.date) >= today)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .slice(0, 10);

  return (
    <div className="calendar-view">
      <div className="page-header">
        <div>
          <h1>📅 Calendar</h1>
          {cohort && <p>{cohort.name}</p>}
          <p className="timezone-notice">⏰ All times shown in Eastern Time (EST/EDT)</p>
        </div>
        
        {isStaff && activeCohortId && (
          <button className="btn-primary" onClick={() => {
            const today = new Date();
            setSelectedDate(today);
            setNewEvent({ ...newEvent, date: formatLocalDate(today) });
            setShowAddModal(true);
          }}>
            + Add Event
          </button>
        )}
      </div>

      {/* Cohort Selector for Admin - Always visible */}
      {isStaff && (
        <div className="cohort-selector">
          <label>Select cohort:</label>
          <select 
            onChange={(e) => setSelectedCohortId(e.target.value || null)} 
            value={activeCohortId || ''}
          >
            <option value="">Choose cohort...</option>
            {cohorts.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Message for students not assigned to a cohort */}
      {!isStaff && !activeCohortId && (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Cohort Assigned</h3>
          <p>You haven't been assigned to a cohort yet. Please contact your administrator to be assigned to a cohort.</p>
        </div>
      )}

      {activeCohortId && (
        <div className="calendar-layout">
          <div className="calendar-main">
            {/* Calendar Navigation */}
            <div className="calendar-nav">
              <button className="nav-btn" onClick={prevMonth}>‹</button>
              <div className="calendar-title">
                <h2>{monthNames[month]} {year}</h2>
                <button className="today-btn" onClick={goToToday}>Today</button>
              </div>
              <button className="nav-btn" onClick={nextMonth}>›</button>
            </div>

            {/* Calendar Grid */}
            <div className="calendar-grid">
              {dayNames.map(day => (
                <div key={day} className="calendar-header-cell">{day}</div>
              ))}
              
              {calendarDays.map((date, index) => {
                if (!date) {
                  return <div key={`empty-${index}`} className="calendar-cell empty"></div>;
                }
                
                const dayEvents = getEventsForDate(date);
                const isToday = date.getTime() === today.getTime();
                
                return (
                  <div 
                    key={date.toISOString()}
                    className={`calendar-cell ${isToday ? 'today' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
                    onClick={() => handleDateClick(date)}
                  >
                    <div className="cell-date">{date.getDate()}</div>
                    <div className="cell-events">
                      {dayEvents.slice(0, 3).map(event => (
                        <div 
                          key={event.id}
                          className={`cell-event ${event.isFromModule ? 'from-module' : ''}`}
                          style={{ backgroundColor: getEventColor(event.type) }}
                          title={`${event.title}${event.isFromModule ? ' (from Master Modules)' : ''}`}
                        >
                          {event.isFromModule && <span className="module-indicator">📚</span>}
                          {event.title.length > (event.isFromModule ? 10 : 12) ? event.title.substring(0, (event.isFromModule ? 10 : 12)) + '...' : event.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="more-events">+{dayEvents.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="calendar-legend">
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: getEventColor('lecture') }}></span>
                Live Lecture
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: getEventColor('assignment') }}></span>
                Assignment
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: getEventColor('quiz') }}></span>
                Quiz
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: getEventColor('exam') }}></span>
                Exam
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ backgroundColor: getEventColor('other') }}></span>
                Other
              </div>
              <div className="legend-item legend-divider">|</div>
              <div className="legend-item">
                <span className="module-indicator">📚</span>
                From Master Modules
              </div>
            </div>
          </div>

          {/* Upcoming Events Sidebar */}
          <div className="calendar-sidebar">
            <h3>Upcoming Events</h3>
            <div className="upcoming-list">
              {upcomingEvents.map(event => (
                <div key={event.id} className="upcoming-item">
                  <div 
                    className="upcoming-dot"
                    style={{ backgroundColor: getEventColor(event.type) }}
                  ></div>
                  <div className="upcoming-info">
                    <div className="upcoming-title">
                      {event.title}
                      {event.isFromModule && (
                        <span className="module-event-badge" title="From Master Modules">📚</span>
                      )}
                      {isStaff && event.isFromMasterTemplate && !event.isFromModule && (
                        <span className="master-calendar-badge" title="Applies to all cohorts">🔗 Master</span>
                      )}
                      {isStaff && event.source === 'cohort_custom' && (
                        <span className="custom-event-badge" title="This cohort only">📌 Custom</span>
                      )}
                    </div>
                    <div className="upcoming-date">
                      {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                      {event.startTime && ` • ${formatTime(event.startTime)}`}
                      {event.isFromModule && event.weekNumber && (
                        <span className="week-indicator"> • Week {event.weekNumber}</span>
                      )}
                    </div>
                    {/* Join Zoom button for live lectures */}
                    {(event.type === 'live-lecture' || event.type === 'lecture') && (
                      <a 
                        href={event.videoUrl || 'https://us02web.zoom.us/j/87587878805?pwd=W9W6aLGWz8F0oKOjAI3nXLccrawXos.1'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-join-zoom-small"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Join Zoom
                      </a>
                    )}
                  </div>
                  {isStaff && !event.isFromModule && (
                    <button 
                      className="btn-icon small"
                      onClick={() => handleDeleteEvent(event.id, event.isFromMasterTemplate)}
                      title={event.isFromMasterTemplate ? 'Delete from all cohorts' : 'Delete from this cohort'}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
              
              {upcomingEvents.length === 0 && (
                <div className="empty-state small">
                  <p>No upcoming events</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Calendar Event</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleAddEvent}>
              {/* Event Scope Toggle - Admin Only */}
              {isStaff && (
                <div className="form-group">
                  <label>Event Scope</label>
                  <div className="scope-toggle-group">
                    <button
                      type="button"
                      className={`scope-btn ${newEvent.eventScope === 'master' ? 'active' : ''}`}
                      onClick={() => setNewEvent(prev => ({...prev, eventScope: 'master', isRecurring: false}))}
                    >
                      <span>🔗</span>
                      <div>
                        <strong>Master Template</strong>
                        <small>Applies to ALL cohorts</small>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={`scope-btn ${newEvent.eventScope === 'cohort' ? 'active' : ''}`}
                      onClick={() => setNewEvent(prev => ({...prev, eventScope: 'cohort'}))}
                    >
                      <span>📌</span>
                      <div>
                        <strong>This Cohort Only</strong>
                        <small>Only for {cohort?.name}</small>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Event Type</label>
                <select 
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({...newEvent, type: e.target.value})}
                >
                  <option value="lecture">🎓 Live Lecture</option>
                  <option value="assignment">📝 Assignment Due</option>
                  <option value="quiz">❓ Quiz</option>
                  <option value="exam">📋 Exam</option>
                  <option value="other">📌 Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Title</label>
                <input 
                  type="text"
                  placeholder="Enter event title..."
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  required
                />
              </div>
              
              {/* Master Template: Week + Day Selection */}
              {newEvent.eventScope === 'master' ? (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Week Number</label>
                      <select 
                        value={newEvent.weekNumber}
                        onChange={(e) => setNewEvent({...newEvent, weekNumber: e.target.value})}
                      >
                        <option value={1}>Week 1</option>
                        <option value={2}>Week 2</option>
                        <option value={3}>Week 3</option>
                        <option value={4}>Week 4</option>
                        <option value={5}>Week 5</option>
                        <option value={6}>Week 6</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>Day of Week</label>
                      <select 
                        value={newEvent.dayOfWeek}
                        onChange={(e) => setNewEvent({...newEvent, dayOfWeek: e.target.value})}
                      >
                        {weekdays.map(day => (
                          <option key={day.key} value={day.key}>{day.key}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="master-template-notice">
                    <span>🔗</span>
                    This event will appear on {newEvent.dayOfWeek} of Week {newEvent.weekNumber} for ALL cohorts
                  </div>
                  
                  {/* Also Add to Module Checkbox */}
                  <div className="form-group">
                    <div 
                      className="toggle-wrapper"
                      onClick={() => setNewEvent(prev => ({...prev, alsoAddToModule: !prev.alsoAddToModule}))}
                    >
                      <div className={`toggle-switch ${newEvent.alsoAddToModule ? 'active' : ''}`}></div>
                      <span className="toggle-label">📚 Also add as a module item in Week {newEvent.weekNumber}</span>
                    </div>
                    {newEvent.alsoAddToModule && (
                      <p className="help-text" style={{ marginTop: '8px', fontSize: '13px', color: 'var(--gray-600)' }}>
                        This will create a matching item in the Master Modules that students can see and track progress on.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                /* Cohort-Specific: Date Selection */
                <div className="form-group">
                  <label>Date</label>
                  <input 
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                    required={newEvent.eventScope === 'cohort'}
                  />
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time (optional)</label>
                  <input 
                    type="time"
                    value={newEvent.startTime}
                    onChange={(e) => setNewEvent({...newEvent, startTime: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>End Time (optional)</label>
                  <input 
                    type="time"
                    value={newEvent.endTime}
                    onChange={(e) => setNewEvent({...newEvent, endTime: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Description (optional)</label>
                <textarea 
                  rows={2}
                  placeholder="Add event details..."
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                />
              </div>

              {/* Recurring Event Toggle - Only for Cohort-Specific Events */}
              {newEvent.eventScope === 'cohort' && (
                <div className="form-group">
                  <div 
                    className="toggle-wrapper"
                    onClick={() => setNewEvent(prev => ({...prev, isRecurring: !prev.isRecurring}))}
                  >
                    <div className={`toggle-switch ${newEvent.isRecurring ? 'active' : ''}`}></div>
                    <span className="toggle-label">🔄 Make this a recurring event</span>
                  </div>
                </div>
              )}

              {/* Recurring Options - Only for Cohort-Specific Events */}
              {newEvent.eventScope === 'cohort' && newEvent.isRecurring && (
                <div className="recurring-options">
                  <div className="recurring-options-header">
                    <span>🔄</span>
                    <span>Recurring Settings</span>
                  </div>

                  <div className="form-group">
                    <label>Repeat</label>
                    <select 
                      value={newEvent.recurringType}
                      onChange={(e) => setNewEvent({...newEvent, recurringType: e.target.value, recurringDays: []})}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {newEvent.recurringType === 'weekly' && (
                    <div className="form-group">
                      <label>Repeat On</label>
                      <div className="weekday-selector">
                        {weekdays.map(day => (
                          <button
                            key={day.key}
                            type="button"
                            className={`weekday-btn ${newEvent.recurringDays.includes(day.key) ? 'active' : ''}`}
                            onClick={() => toggleRecurringDay(day.key)}
                          >
                            {day.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>End After</label>
                      <select 
                        value={newEvent.recurringEndDate ? 'date' : 'count'}
                        onChange={(e) => {
                          if (e.target.value === 'count') {
                            setNewEvent({...newEvent, recurringEndDate: ''});
                          }
                        }}
                      >
                        <option value="count">Number of occurrences</option>
                        <option value="date">Specific date</option>
                      </select>
                    </div>

                    {!newEvent.recurringEndDate ? (
                      <div className="form-group">
                        <label>Occurrences</label>
                        <select 
                          value={newEvent.recurringCount}
                          onChange={(e) => setNewEvent({...newEvent, recurringCount: parseInt(e.target.value)})}
                        >
                          <option value={2}>2 times</option>
                          <option value={4}>4 times</option>
                          <option value={6}>6 times</option>
                          <option value={8}>8 times</option>
                          <option value={10}>10 times</option>
                          <option value={12}>12 times</option>
                        </select>
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>End Date</label>
                        <input 
                          type="date"
                          value={newEvent.recurringEndDate}
                          onChange={(e) => setNewEvent({...newEvent, recurringEndDate: e.target.value})}
                          min={newEvent.date}
                        />
                      </div>
                    )}
                  </div>

                  {/* Show end by date option */}
                  {!newEvent.recurringEndDate && (
                    <div className="form-group" style={{ marginTop: '8px' }}>
                      <button 
                        type="button" 
                        className="btn-link"
                        onClick={() => {
                          const endDate = parseLocalDate(newEvent.date);
                          endDate.setMonth(endDate.getMonth() + 2);
                          setNewEvent({...newEvent, recurringEndDate: formatLocalDate(endDate)});
                        }}
                      >
                        Or set an end date instead →
                      </button>
                    </div>
                  )}

                  {newEvent.recurringEndDate && (
                    <div className="form-group" style={{ marginTop: '8px' }}>
                      <button 
                        type="button" 
                        className="btn-link"
                        onClick={() => setNewEvent({...newEvent, recurringEndDate: ''})}
                      >
                        Or use occurrence count instead →
                      </button>
                    </div>
                  )}

                  {getRecurringPreview() && (
                    <div className="recurring-preview">
                      <span className="preview-icon">📅</span>
                      <span>{getRecurringPreview()}</span>
                    </div>
                  )}
                </div>
              )}
              
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {newEvent.eventScope === 'master' 
                    ? '🔗 Add to All Cohorts' 
                    : newEvent.isRecurring 
                      ? 'Create Recurring Events' 
                      : 'Add Event'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Day Detail Modal - Shows events for selected day */}
      {showDayModal && selectedDate && (
        <div className="modal-overlay" onClick={() => setShowDayModal(false)}>
          <div className="modal day-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  month: 'long', 
                  day: 'numeric',
                  year: 'numeric'
                })}
              </h3>
              <button className="modal-close" onClick={() => setShowDayModal(false)}>×</button>
            </div>
            
            <div className="day-events-list">
              {selectedDayEvents.length === 0 ? (
                <div className="empty-state small">
                  <p>No events on this day</p>
                </div>
              ) : (
                selectedDayEvents.map(event => (
                  <div key={event.id} className="day-event-item">
                    <div 
                      className="day-event-color"
                      style={{ backgroundColor: getEventColor(event.type) }}
                    ></div>
                    <div className="day-event-info">
                      <div className="day-event-title">
                        {event.isFromModule && <span className="module-indicator">📚</span>}
                        {event.title}
                      </div>
                      <div className="day-event-meta">
                        <span className="event-type-badge">{event.type}</span>
                        {event.startTime && (
                          <span className="event-time">
                            {formatTime(event.startTime)}
                            {event.endTime && ` - ${formatTime(event.endTime)}`}
                          </span>
                        )}
                      </div>
                      {event.description && (
                        <div className="day-event-description">{event.description}</div>
                      )}
                      {/* Join Lecture button for live lectures */}
                      {(event.type === 'live-lecture' || event.type === 'lecture') && (
                        <a 
                          href={event.videoUrl || 'https://us02web.zoom.us/j/87587878805?pwd=W9W6aLGWz8F0oKOjAI3nXLccrawXos.1'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-join-zoom-modal"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img 
                            src="https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Zoom_Logo_2022.svg/2560px-Zoom_Logo_2022.svg.png"
                            alt="Zoom"
                            style={{ height: '16px', width: 'auto' }}
                          />
                          Join Lecture
                        </a>
                      )}
                    </div>
                    {isStaff && !event.isFromModule && (
                      <button 
                        className="btn-icon small danger"
                        onClick={() => {
                          handleDeleteEvent(event.id, event.isFromMasterTemplate);
                          setSelectedDayEvents(prev => prev.filter(e => e.id !== event.id));
                        }}
                        title="Delete event"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
            
            {isStaff && activeCohortId && (
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-primary"
                  onClick={handleAddEventFromDayModal}
                >
                  + Add Event
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
